import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { jsPDF } from 'jspdf';
import '../styles/QuickSaleForm.css';

const SellerQuickSaleForm = ({ onSaleAdded, onCancel }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    sale_date: new Date().toISOString().split('T')[0], // Fecha actual por defecto
    total_amount: '',
    payment_method: 'Efectivo', // Valor por defecto
    point_of_sale_id: '',
    description: ''
  });
  const [printTicket, setPrintTicket] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [assignedPointOfSale, setAssignedPointOfSale] = useState(null);
  const [loadingPointOfSale, setLoadingPointOfSale] = useState(true);
  const [isGeneratingTicket, setIsGeneratingTicket] = useState(false);
  const [businessDateTime, setBusinessDateTime] = useState(null);

  // Función para obtener la fecha y hora del negocio
  const fetchBusinessTime = async (businessId) => {
    if (!businessId) return null;
    
    try {
      console.log('Obteniendo fecha y hora del negocio para el registro de venta...');
      const serverTimeResponse = await axios.get('/api/seller/time', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        timeout: 5000
      });
      
      console.log('Respuesta de fecha/hora del negocio:', serverTimeResponse.data);
      
      // La función get_business_time devuelve la fecha y hora ya ajustada al huso horario del negocio
      const serverTimeStr = serverTimeResponse.data.time;
      
      if (serverTimeStr && typeof serverTimeStr === 'string') {
        // Guardar la fecha/hora completa del negocio
        setBusinessDateTime(serverTimeStr);
        console.log('Fecha/hora completa del negocio para registro en BD:', serverTimeStr);
        return serverTimeStr;
      }
      return null;
    } catch (error) {
      console.error('Error al obtener la fecha/hora del negocio:', error);
      return null;
    }
  };

  useEffect(() => {
    const fetchAssignedPointOfSale = async () => {
      try {
        setLoadingPointOfSale(true);
        const token = localStorage.getItem('token');
        
        if (!token) {
          setError('No se encontró un token de autenticación. Por favor, inicie sesión nuevamente.');
          return;
        }

        const response = await axios.get('/api/points-of-sale/assigned', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        if (response.data && response.data.pointsOfSale && response.data.pointsOfSale.length > 0) {
          const pos = response.data.pointsOfSale[0];
          setAssignedPointOfSale(pos);
          
          // Establecer el punto de venta asignado en el formulario
          setFormData(prevState => ({
            ...prevState,
            point_of_sale_id: pos.id
          }));
          
          // Obtener la fecha y hora del negocio
          await fetchBusinessTime(pos.business_id);
        } else {
          setError('No tienes un punto de venta asignado. Por favor, contacta al administrador.');
        }
      } catch (error) {
        console.error('Error al obtener punto de venta asignado:', error);
        setError('Error al cargar el punto de venta asignado. Por favor, inténtelo de nuevo.');
      } finally {
        setLoadingPointOfSale(false);
      }
    };

    fetchAssignedPointOfSale();
  }, [user]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      if (name === 'printTicket') {
        setPrintTicket(checked);
      }
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };
  
  // Función para generar el ticket de venta en PDF con la zona horaria del negocio
  const generateTicket = async (saleData) => {
    try {
      console.log('Iniciando generación de ticket PDF...');
      setIsGeneratingTicket(true);
      let formattedDate = '';
      let formattedTime = '';
      let businessTimezone = 'America/Mexico_City';
      
      // Obtener el business_id del punto de venta asignado
      let businessId = null;
      if (assignedPointOfSale && assignedPointOfSale.business_id) {
        businessId = assignedPointOfSale.business_id;
        console.log('Business ID para obtener hora:', businessId);
      }
      
      // Intentar obtener la hora del servidor usando el endpoint específico para vendedores
      // Este endpoint llama a la función get_business_time de Supabase
      try {
        console.log('Intentando obtener la hora del servidor con get_business_time...');
        const serverTimeResponse = await axios.get('/api/seller/time', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          timeout: 5000 // Aumentar timeout a 5 segundos para dar tiempo a la función RPC
        });
        
        console.log('RESPUESTA de get_business_time:', serverTimeResponse.data);
        
        // IMPORTANTE: La función get_business_time devuelve la fecha y hora ya ajustada al huso horario del negocio
        // pero como string. Debemos extraer directamente las partes de fecha y hora sin crear un objeto Date
        // que reinterpretaría la zona horaria.
        const serverTimeStr = serverTimeResponse.data.time;
        businessTimezone = serverTimeResponse.data.timezone || 'America/Mexico_City';
        
        try {
          // La respuesta de get_business_time es un string ISO con formato: "2025-06-07T13:05:19.123456+00:00"
          // Necesitamos extraer directamente las partes de la fecha y hora sin crear un objeto Date
          console.log('Fecha y hora recibida del servidor:', serverTimeStr);
          
          if (!serverTimeStr || typeof serverTimeStr !== 'string') {
            throw new Error('Formato de fecha inválido recibido del servidor');
          }
          
          // Extraer directamente las partes de la fecha y hora del string devuelto por get_business_time
          // Este enfoque evita la reinterpretación de zona horaria que ocurre al crear un objeto Date
          const dateParts = serverTimeStr.split('T');
          
          if (dateParts.length !== 2) {
            throw new Error('Formato de fecha/hora inesperado');
          }
          
          // Extraer la fecha (YYYY-MM-DD)
          const rawDate = dateParts[0];
          const dateComponents = rawDate.split('-');
          if (dateComponents.length !== 3) {
            throw new Error('Formato de fecha inesperado');
          }
          
          // Formatear la fecha como DD/MM/YYYY
          formattedDate = `${dateComponents[2]}/${dateComponents[1]}/${dateComponents[0]}`;
          
          // Extraer la hora (HH:MM:SS)
          const rawTime = dateParts[1].split('.')[0]; // Eliminar milisegundos y zona horaria
          formattedTime = rawTime;
          
          console.log('Fecha extraída directamente:', formattedDate);
          console.log('Hora extraída directamente:', formattedTime);
          console.log('Zona horaria del negocio:', businessTimezone);
          
        } catch (formatError) {
          console.error('Error al extraer la fecha y hora:', formatError);
          // En lugar de lanzar un error, usar la hora local como respaldo
          throw new Error('Error al procesar la fecha del servidor');
        }
      } catch (serverTimeError) {
        console.error('Error al obtener la hora del servidor con get_business_time:', serverTimeError);
        console.log('Usando fecha y hora local como alternativa...');
        
        // Usar la fecha y hora local como respaldo
        const now = new Date();
        formattedDate = now.toLocaleDateString('es-MX', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });
        formattedTime = now.toLocaleTimeString('es-MX', { 
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false 
        });
        
        console.log('Fecha local (respaldo):', formattedDate);
        console.log('Hora local (respaldo):', formattedTime);
      }
      
      console.log('RESULTADO FINAL:');
      console.log('Fecha que se mostrará en el ticket:', formattedDate);
      console.log('Hora que se mostrará en el ticket:', formattedTime);
      
      // Obtener datos del negocio asociado al punto de venta asignado
      let business = {};
      let user = {};
      
      // Verificar si tenemos el punto de venta asignado y su business_id
      if (!assignedPointOfSale || !assignedPointOfSale.business_id) {
        console.error('No se encontró el punto de venta asignado o su business_id');
        // Intentar obtener el punto de venta asignado nuevamente
        try {
          const posResponse = await axios.get('/api/points-of-sale/assigned', {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            timeout: 5000
          });
          
          if (posResponse.data && posResponse.data.pointsOfSale && posResponse.data.pointsOfSale.length > 0) {
            const pos = posResponse.data.pointsOfSale[0];
            console.log('Punto de venta obtenido:', pos);
            if (pos.business_id) {
              // Ahora intentamos obtener los datos del negocio directamente usando el business_id
              try {
                console.log('Intentando obtener datos del negocio con ID:', pos.business_id);
                const businessDetailResponse = await axios.get(`/api/businesses/${pos.business_id}`, {
                  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                  timeout: 5000
                });
                
                if (businessDetailResponse.data) {
                  business = businessDetailResponse.data;
                  console.log('Datos del negocio obtenidos directamente con business_id:', business);
                  console.log('Nombre del negocio:', business.bis_name);
                  console.log('Teléfono del negocio:', business.bis_phone);
                }
              } catch (businessError) {
                console.error('Error al obtener datos del negocio con business_id desde punto de venta:', businessError);
              }
            }
          }
        } catch (posError) {
          console.error('Error al obtener el punto de venta asignado:', posError);
        }
      } else {
        // Tenemos el punto de venta asignado y su business_id, obtener los datos del negocio directamente
        try {
          console.log('Obteniendo datos del negocio con business_id:', assignedPointOfSale.business_id);
          const businessDetailResponse = await axios.get(`/api/businesses/${assignedPointOfSale.business_id}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            timeout: 5000
          });
          
          if (businessDetailResponse.data) {
            business = businessDetailResponse.data;
            console.log('Datos del negocio obtenidos con business_id:', business);
            console.log('Nombre del negocio:', business.bis_name);
            console.log('Teléfono del negocio:', business.bis_phone);
          } else {
            console.error('No se obtuvieron datos del negocio');
          }
        } catch (businessError) {
          console.error('Error al obtener datos del negocio con business_id:', businessError);
          console.log('Error completo:', JSON.stringify(businessError));
          
          // Intentar obtener los datos del negocio a través de /api/businesses/me como respaldo
          try {
            console.log('Intentando obtener datos del negocio con /api/businesses/me como respaldo');
            const businessResponse = await axios.get('/api/businesses/me', {
              headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
              timeout: 5000
            });
            business = businessResponse.data;
            console.log('Datos del negocio obtenidos como respaldo:', business);
            console.log('Nombre del negocio (respaldo):', business.bis_name);
            console.log('Teléfono del negocio (respaldo):', business.bis_phone);
          } catch (backupError) {
            console.error('Error al obtener datos del negocio como respaldo:', backupError);
            // Continuar con valores predeterminados
            business = { bis_name: 'Negocio', bis_phone: 'No disponible' };
          }
        }
      }
      
      // Si después de todos los intentos no tenemos los datos del negocio, usar valores predeterminados
      if (!business || !business.bis_name || !business.bis_phone) {
        console.warn('No se obtuvieron datos completos del negocio, usando valores predeterminados');
        business = { 
          ...business,
          bis_name: business.bis_name || 'Negocio', 
          bis_phone: business.bis_phone || 'No disponible' 
        };
      }
      
      // Obtener datos del usuario
      try {
        const userResponse = await axios.get('/api/users/me', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          timeout: 3000
        });
        user = userResponse.data;
        console.log('Datos del usuario obtenidos:', user);
      } catch (userError) {
        console.error('Error al obtener datos del usuario:', userError);
        // Continuar con valores predeterminados
        user = { full_name: 'Usuario' };
      }
      
      // Crear el documento PDF con ancho de 58mm (ticket pequeño)
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: [58, 200] // Ancho de 58mm, altura ajustable
      });
      
      doc.setFont('helvetica');
      doc.setFontSize(10);
      
      // Encabezado del ticket
      doc.setFontSize(10);
      doc.text(business.bis_name || 'Negocio', 29, 10, null, null, 'center');
      doc.text('Ticket de Venta', 29, 15, null, null, 'center');
      doc.text(`Tel: ${business.bis_phone || 'No disponible'}`, 29, 20, null, null, 'center');
      doc.text('Venta Rápida', 29, 25, null, null, 'center');
      
      // Información de la venta
      const displayPosName = assignedPointOfSale ? assignedPointOfSale.pos_name : 'No disponible';
      
      doc.text(`Punto de venta: ${displayPosName}`, 2, 35);
      // Truncar el nombre del usuario a 12 caracteres
      const userName = user && user.full_name ? user.full_name.substring(0, 12) : 'No disponible';
      doc.text(`Vendedor: ${userName}`, 2, 40);
      doc.text(`Fecha: ${formattedDate || 'No disponible'}`, 2, 45);
      doc.text(`Hora: ${formattedTime || 'No disponible'} (${businessTimezone || 'UTC'})`, 2, 50);
      
      // Línea divisoria
      doc.line(2, 60, 56, 60);
      doc.setFontSize(9);
      doc.text('Detalle de la venta', 29, 65, null, null, 'center');
      doc.setFontSize(8);
      doc.text('Producto', 2, 70);
      doc.text('Total', 52, 70, null, null, 'right');
      
      // Línea divisoria
      doc.line(2, 75, 56, 75);
      
      // Para venta rápida, mostrar una línea genérica
      let y = 80;
      doc.text('Venta rápida', 2, y);
      doc.text(`$${saleData.total.toFixed(2)}`, 52, y, null, null, 'right');
      y += 5;
      
      // Línea divisoria
      doc.line(2, y, 56, y);
      y += 5;
      
      // Totales
      const subtotal = saleData.total;
      
      doc.text(`Subtotal: $${subtotal.toFixed(2)}`, 52, y, null, null, 'right');
      y += 5;
      doc.setFontSize(10);
      doc.text(`Total: $${saleData.total.toFixed(2)}`, 52, y, null, null, 'right');
      doc.setFontSize(8);
      y += 10;
      
      // Método de pago
      doc.text(`Método de pago: ${saleData.paymentMethod || 'Efectivo'}`, 2, y);
      y += 10;
      
      // Pie de página
      doc.text('Gracias por su compra', 29, y, null, null, 'center');
      y += 5;
      
      // Crear ID de venta único
      const dateTimeStr = formattedDate.replace(/\//g, '') + formattedTime.replace(/:/g, '');
      const idPosName = displayPosName.replace(/\s+/g, '');
      const userNameStr = userName.replace(/\s+/g, '');
      const totalInt = Math.floor(saleData.total);
      const saleIdUnique = `${dateTimeStr}${idPosName}${userNameStr}${totalInt}`;
      
      doc.text('IdVenta:', 2, y);
      y += 5;
      
      // Dividir el ID de venta en múltiples líneas si es necesario
      if (saleIdUnique.length > 37) {
        const numLines = Math.ceil(saleIdUnique.length / 37);
        for (let i = 0; i < numLines; i++) {
          const startIndex = i * 37;
          const endIndex = Math.min(startIndex + 37, saleIdUnique.length);
          const linePart = saleIdUnique.substring(startIndex, endIndex);
          
          doc.text(linePart, 2, y);
          
          if (i < numLines - 1) {
            y += 5;
          }
        }
      } else {
        doc.text(saleIdUnique, 2, y);
      }
      
      // Generar el nombre del archivo con la fecha y hora
      const dateForFilename = formattedDate.replace(/\//g, '-');
      const timeForFilename = formattedTime.replace(/:/g, '-').replace(/\s/g, '');
      const filename = `ticket_venta_${dateForFilename}_${timeForFilename}.pdf`;
      
      console.log('Intentando descargar el PDF...');
      
      // Simplificar el proceso de descarga para evitar problemas
      console.log('Intentando descargar el PDF...');
      
      // Método 1: Método directo de jsPDF (más confiable)
      try {
        console.log('Usando método doc.save() directo');
        doc.save(filename);
        console.log('PDF guardado con método directo');
        setIsGeneratingTicket(false);
        return true;
      } catch (saveError) {
        console.error('Error con método directo:', saveError);
      }
      
      // Método 2: Usando Blob y createObjectURL como respaldo
      try {
        const blob = doc.output('blob');
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.style.display = 'none';
        document.body.appendChild(link);
        
        console.log('Descargando con Blob y URL.createObjectURL');
        link.click();
        
        setTimeout(() => {
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          console.log('Limpieza de recursos completada');
        }, 1000);
        
        console.log('PDF descargado con método Blob');
        setIsGeneratingTicket(false);
        return true;
      } catch (blobError) {
        console.error('Error con método Blob:', blobError);
      }
      
      // Método 3: Usando base64 y una nueva ventana como último recurso
      try {
        const base64 = doc.output('datauristring');
        const newWindow = window.open();
        if (newWindow) {
          newWindow.document.write(`<iframe width='100%' height='100%' src='${base64}'></iframe>`);
          newWindow.document.title = filename;
          newWindow.document.close();
          console.log('PDF abierto en nueva ventana');
          setIsGeneratingTicket(false);
          return true;
        }
      } catch (base64Error) {
        console.error('Error con método base64:', base64Error);
      }
      
      // Si llegamos aquí, todos los métodos fallaron
      console.error('Todos los métodos de descarga fallaron');
      setIsGeneratingTicket(false);
      throw new Error('No se pudo descargar el PDF después de intentar múltiples métodos');
      
      setIsGeneratingTicket(false);
      return true;
    } catch (error) {
      console.error('Error al generar el ticket:', error);
      setIsGeneratingTicket(false);
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('No se encontró un token de autenticación. Por favor, inicie sesión nuevamente.');
        return;
      }

      // Validar campos requeridos
      if (!formData.sale_date || !formData.total_amount || !formData.point_of_sale_id || !formData.payment_method) {
        setError('Todos los campos son obligatorios');
        setLoading(false);
        return;
      }

      // Validar que el monto total sea un número positivo
      if (isNaN(formData.total_amount) || parseFloat(formData.total_amount) <= 0) {
        setError('El monto total debe ser un número positivo');
        setLoading(false);
        return;
      }

      // Actualizar la fecha y hora del negocio antes de enviar
      let currentBusinessTime = businessDateTime;
      if (assignedPointOfSale?.business_id) {
        const freshBusinessTime = await fetchBusinessTime(assignedPointOfSale.business_id);
        if (freshBusinessTime) {
          currentBusinessTime = freshBusinessTime;
        }
      }
      
      // Combinar la fecha seleccionada por el usuario con la zona horaria del negocio
      let finalSaleDate = formData.sale_date;
      
      try {
        // Si tenemos la fecha/hora del negocio y el usuario ha seleccionado una fecha
        if (currentBusinessTime && formData.sale_date) {
          console.log('Fecha seleccionada por el usuario (original):', formData.sale_date);
          
          // Extraer componentes de la fecha seleccionada por el usuario
          // Formato esperado: YYYY-MM-DD
          const [userYear, userMonth, userDay] = formData.sale_date.split('-').map(Number);
          
          // Crear objeto Date con la fecha/hora del negocio
          const businessDate = new Date(currentBusinessTime);
          console.log('Fecha/hora del negocio (original):', businessDate.toISOString());
          
          // Crear una nueva fecha combinando la fecha del usuario con la hora del negocio
          // IMPORTANTE: Usamos UTC para evitar problemas de zona horaria
          const combinedDate = new Date(Date.UTC(
            userYear,
            userMonth - 1, // Meses en JavaScript son 0-indexed
            userDay,
            businessDate.getUTCHours(),
            businessDate.getUTCMinutes(),
            businessDate.getUTCSeconds(),
            businessDate.getUTCMilliseconds()
          ));
          
          // Convertir a string ISO
          finalSaleDate = combinedDate.toISOString();
          console.log('Fecha combinada (UTC, fecha del usuario + hora del negocio):', finalSaleDate);
        } else {
          // Si no tenemos fecha/hora del negocio, usar la fecha seleccionada por el usuario
          console.log('Usando fecha seleccionada por el usuario:', formData.sale_date);
        }
      } catch (dateError) {
        console.error('Error al combinar fecha del usuario con zona horaria del negocio:', dateError);
        // En caso de error, usar la fecha seleccionada por el usuario como respaldo
      }
      
      console.log('Fecha final a registrar:', finalSaleDate);
      
      // Enviar datos al servidor con la fecha combinada (fecha del usuario + zona horaria del negocio)
      await axios.post('/api/sales/quick', 
        {
          sale_date: finalSaleDate, // Usar la fecha seleccionada por el usuario con la zona horaria del negocio
          total_amount: parseFloat(formData.total_amount),
          payment_method: formData.payment_method,
          point_of_sale_id: formData.point_of_sale_id,
          description: formData.description && formData.description.trim() !== '' ? formData.description.trim() : null
        }, 
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      // Limpiar el formulario
      setFormData({
        sale_date: new Date().toISOString().split('T')[0],
        total_amount: '',
        payment_method: 'Efectivo', // Mantener el valor por defecto
        point_of_sale_id: formData.point_of_sale_id, // Mantener el punto de venta seleccionado
        description: ''
      });

      // Si la casilla de imprimir ticket está marcada
      if (printTicket) {
        setSuccessMessage('Venta registrada. Generando ticket...');
        
        // Generar el ticket con un pequeño retraso para asegurar que la UI se actualice
        setTimeout(async () => {
          try {
            console.log('Generando ticket desde handleSubmit');
            
            // Verificar que el punto de venta asignado exista
            if (!assignedPointOfSale) {
              console.error('Error: No hay punto de venta asignado');
              throw new Error('No se pudo obtener información del punto de venta');
            }
            
            console.log('Datos para el ticket:', {
              pointOfSaleId: formData.point_of_sale_id,
              pointOfSaleName: assignedPointOfSale.pos_name,
              paymentMethod: formData.payment_method,
              total: parseFloat(formData.total_amount)
            });
            
            const saleData = {
              pointOfSaleId: formData.point_of_sale_id,
              pointOfSaleName: assignedPointOfSale.pos_name,
              paymentMethod: formData.payment_method,
              total: parseFloat(formData.total_amount)
            };
            
            try {
              const ticketGenerated = await generateTicket(saleData);
              
              if (ticketGenerated) {
                // Actualizar el mensaje después de generar el ticket
                setSuccessMessage('Venta registrada exitosamente. Ticket generado.');
              } else {
                console.warn('La función generateTicket devolvió false');
                setSuccessMessage('Venta registrada exitosamente. No se pudo generar el ticket.');
              }
            } catch (ticketGenerationError) {
              console.error('Error en la generación del ticket:', ticketGenerationError);
              // Aún así, la venta se registró correctamente
              setSuccessMessage('Venta registrada exitosamente. No se pudo generar el ticket.');
            }
            
            // Limpiar el mensaje después de 5 segundos
            setTimeout(() => {
              setSuccessMessage('');
            }, 5000);
          } catch (ticketError) {
            console.error('Error al generar ticket:', ticketError);
            setError('Error al generar el ticket: ' + ticketError.message);
            
            // Limpiar el mensaje de error después de 5 segundos
            setTimeout(() => {
              setError('');
            }, 5000);
          }
        }, 500);
      } else {
        // Si no está marcada la casilla, mostrar mensaje de éxito normal
        setSuccessMessage('Venta registrada exitosamente.');
        
        // Limpiar el mensaje después de 5 segundos
        setTimeout(() => {
          setSuccessMessage('');
        }, 5000);
      }
      
      // Mantener compatibilidad con la interfaz pero sin activar el popup
      // onSaleAdded(); // Comentado para evitar que se muestre el popup
    } catch (error) {
      console.error('Error al registrar la venta:', error);
      setError(error.response?.data?.message || 'Error al registrar la venta. Por favor, inténtelo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="quick-sale-form-container">
      <h2>Registro de Venta Rápida</h2>
      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}
      
      {loadingPointOfSale ? (
        <div className="loading-message">Cargando punto de venta...</div>
      ) : !assignedPointOfSale ? (
        <div className="error-message">No tienes un punto de venta asignado. Por favor, contacta al administrador.</div>
      ) : (
        <div className="point-of-sale-info">
          <p><strong>Punto de Venta:</strong> {assignedPointOfSale.pos_name}</p>
        </div>
      )}
      
      <form className="quick-sale-form" onSubmit={handleSubmit}>
        <input 
          type="hidden" 
          name="point_of_sale_id" 
          value={formData.point_of_sale_id} 
        />
        <div className="form-group">
          <label htmlFor="sale_date">Fecha de Venta *</label>
          <input
            type="date"
            id="sale_date"
            name="sale_date"
            value={formData.sale_date}
            onChange={handleChange}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="total_amount">Monto Total *</label>
          <div className="input-with-icon">
            <span className="currency-symbol">$</span>
            <input
              type="number"
              id="total_amount"
              name="total_amount"
              value={formData.total_amount}
              onChange={handleChange}
              step="0.01"
              min="0.01"
              placeholder="0.00"
              required
            />
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="description">Detalle de la Venta (opcional)</label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={3}
            placeholder="Registra los productos o servicios separados por coma ej: GloboMetalico, CuadernoDeRayas, BlusaBlancaS, VelaBautizo"
          />
        </div>
        <div className="form-group">
          <label htmlFor="payment_method">Método de Pago *</label>
          <div className="payment-method-options">
            <div className={`payment-option ${formData.payment_method === 'Efectivo' ? 'selected' : ''}`}>
              <input
                type="radio"
                id="payment_cash"
                name="payment_method"
                value="Efectivo"
                checked={formData.payment_method === 'Efectivo'}
                onChange={handleChange}
                required
              />
              <label htmlFor="payment_cash">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="6" width="20" height="12" rx="2" />
                  <circle cx="12" cy="12" r="2" />
                  <path d="M6 12h.01M18 12h.01" />
                </svg>
                Efectivo
              </label>
            </div>
            <div className={`payment-option ${formData.payment_method === 'Tarjeta' ? 'selected' : ''}`}>
              <input
                type="radio"
                id="payment_card"
                name="payment_method"
                value="Tarjeta"
                checked={formData.payment_method === 'Tarjeta'}
                onChange={handleChange}
              />
              <label htmlFor="payment_card">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                  <line x1="1" y1="10" x2="23" y2="10" />
                </svg>
                Tarjeta
              </label>
            </div>
            <div className={`payment-option ${formData.payment_method === 'Transferencia' ? 'selected' : ''}`}>
              <input
                type="radio"
                id="payment_transfer"
                name="payment_method"
                value="Transferencia"
                checked={formData.payment_method === 'Transferencia'}
                onChange={handleChange}
              />
              <label htmlFor="payment_transfer">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="1" x2="12" y2="23" />
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                  <polyline points="16 3 12 7 8 3" />
                </svg>
                Transferencia
              </label>
            </div>
          </div>
        </div>
        <div className="form-group print-ticket-option">
          <label className="checkbox-container">
            <input
              type="checkbox"
              name="printTicket"
              checked={printTicket}
              onChange={handleChange}
            />
            <span className="checkbox-label">Generar Ticket de Venta (58mm)</span>
          </label>
        </div>
        <div className="form-buttons">
          <button type="button" className="cancel-btn" onClick={onCancel} disabled={loading}>
            Cancelar
          </button>
          <button type="submit" className="submit-btn" disabled={loading || !assignedPointOfSale}>
            {loading ? 'Registrando...' : 'Registrar Venta'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SellerQuickSaleForm;
